import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class VoiceVisualService {
    private levelSub = new BehaviorSubject<number>(0); // 0..1
    level$(): Observable<number> { return this.levelSub.asObservable(); }

    private audioCtx: AudioContext | null = null;
    private analyser: AnalyserNode | null = null;
    private micStream: MediaStream | null = null;
    private rafId: number | null = null;

    private fakeTimer: number | null = null;
    private fakeStart = 0;
    private fakeDuration = 0;

    /** Real mic level (best for "listening") */
    async startMic(): Promise<void> {
        this.stopFake();

        if (this.micStream) return; // already running
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.micStream = stream;

        this.audioCtx = new AudioContext();
        const source = this.audioCtx.createMediaStreamSource(stream);
        this.analyser = this.audioCtx.createAnalyser();
        this.analyser.fftSize = 512;

        source.connect(this.analyser);

        const data = new Uint8Array(this.analyser.frequencyBinCount);

        const tick = () => {
            if (!this.analyser) return;

            this.analyser.getByteTimeDomainData(data);

            // RMS on waveform -> 0..1-ish
            let sum = 0;
            for (let i = 0; i < data.length; i++) {
                const v = (data[i] - 128) / 128;
                sum += v * v;
            }
            const rms = Math.sqrt(sum / data.length); // ~0..1

            // smooth + clamp
            const level = Math.min(1, rms * 2.2);
            this.levelSub.next(level);

            this.rafId = requestAnimationFrame(tick);
        };

        this.rafId = requestAnimationFrame(tick);
    }

    async stopMic(): Promise<void> {
        if (this.rafId != null) cancelAnimationFrame(this.rafId);
        this.rafId = null;

        this.analyser?.disconnect();
        this.analyser = null;

        if (this.audioCtx) await this.audioCtx.close().catch(() => { });
        this.audioCtx = null;

        this.micStream?.getTracks().forEach(t => t.stop());
        this.micStream = null;

        this.levelSub.next(0);
    }

    /** Fake level (best for native TTS speaking) */
    onSpeakingStart(text: string): void {
        // crude duration estimate: chars / cps
        const cps = 14; // characters per second, tweak for your voice
        this.fakeDuration = Math.max(800, Math.min(8000, (text.length / cps) * 1000));
        this.fakeStart = performance.now();

        this.stopMic().catch(() => { });
        this.startFake();
    }

    onSpeakingEnd(): void {
        this.stopFake();
        this.levelSub.next(0);
    }

    private startFake(): void {
        this.stopFake();

        this.fakeTimer = window.setInterval(() => {
            const t = performance.now() - this.fakeStart;
            const p = Math.min(1, t / this.fakeDuration);

            // envelope: ramp up, then down
            const env = p < 0.15 ? (p / 0.15) : (p > 0.9 ? (1 - p) / 0.1 : 1);

            // add some "speechy" wobble
            const wobble =
                0.55 +
                0.25 * Math.sin(t / 70) +
                0.18 * Math.sin(t / 120) +
                0.12 * Math.sin(t / 45);

            const level = Math.max(0, Math.min(1, env * wobble));
            this.levelSub.next(level);

            if (p >= 1) this.onSpeakingEnd();
        }, 16);
    }

    private stopFake(): void {
        if (this.fakeTimer != null) {
            clearInterval(this.fakeTimer);
            this.fakeTimer = null;
        }
    }
}
